import { notification } from 'antd';
import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import type { FilteredInfo, RequestParams, TableChange } from '../../../models/table';
import { SortedInfo } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import { removeEmpty } from '../../../service/table.service';

const { logError } = RaygunErrorHandlerService();

export type AdminSite = {
  id: string | null;
  name: string | null;
  city: string | null;
  address: string | null;
  country: 'Canada' | 'United States';
  phone: string | null;
  postal_code: string | null;
  state: string | null;
  email: string | null;
  organization: string | null;
  organization_id: string | null;
  latitude: number | null;
  longitude: number | null;
  total_count?: number;
  clinicians?: string[];
  client_assignments?: number;
  clinician_assignments?: number;
  archived?: boolean;
};

export const createAdminSite = (): AdminSite => ({
  id: null,
  name: null,
  city: null,
  address: null,
  country: 'Canada',
  phone: null,
  postal_code: null,
  state: null,
  email: null,
  organization: null,
  organization_id: null,
  latitude: null,
  longitude: null,
});

export const ADMIN_SITES_MODAL = {
  IDLE: 'idle',
  EDIT_SITE: 'edit_site',
  DELETE_SITE: 'delete_site',
  ARCHIVE_SITE: 'archive_site',
} as const;

type ADMIN_SITES_MODAL = (typeof ADMIN_SITES_MODAL)[keyof typeof ADMIN_SITES_MODAL];

type AdminSitesSliceType = {
  adminSites: AdminSite[];
  requestAdminSitesParam: RequestParams<AdminSite>;
  loadingAdminSites: boolean;
  submittingAdminSite: boolean;
  modal: ADMIN_SITES_MODAL;
  adminSite: AdminSite;
};

const initialState: AdminSitesSliceType = {
  adminSites: [],
  requestAdminSitesParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<AdminSite>('name', 'ascend'),
    filteredInfo: {},
  },
  loadingAdminSites: false,
  submittingAdminSite: false,
  modal: ADMIN_SITES_MODAL.IDLE,
  adminSite: createAdminSite(),
};

export const fetchAdminSites = createAsyncThunk<AdminSite[], undefined, AsyncThunkConfig>(
  'adminSites/fetchAdminSites',
  async (_, thunkAPI) => {
    try {
      const params = thunkAPI.getState().adminSitesSlice.requestAdminSitesParam;
      const response = (await axios.post('v2_admin_site', params, {
        signal: thunkAPI.signal,
      })) as AdminSite[];
      return response ?? [];
    } catch (e) {
      logError(e, ['adminSitesSlice', 'fetchAdminSites']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchAllSites = createAsyncThunk<AdminSite[], undefined, AsyncThunkConfig>(
  'adminSites/fetchAllSites',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.post('v2_admin_site', undefined, {
        signal: thunkAPI.signal,
      })) as AdminSite[];
      return response ?? [];
    } catch (e) {
      logError(e, ['adminSitesSlice', 'fetchAllSites']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export type DeleteAdminSiteParam = { id: string; organization_id: string };

export const deleteAdminSite = createAsyncThunk<string | null, DeleteAdminSiteParam, AsyncThunkConfig>(
  'adminSites/deleteSite',
  async ({ id, organization_id }, thunkAPI) => {
    try {
      const response = (await axios.post('v2_admin_site_delete', { id, organization_id })) as string; //deleted_site_id;
      if (response) {
        notification.success({
          title: 'Site deleted successfully',
          description: 'Success',
        });
        await thunkAPI.dispatch(fetchAdminSites());
        thunkAPI.dispatch(closeAdminSiteModal());
      }
      return response ?? '';
    } catch (e) {
      logError(e, ['adminSitesSlice', 'deleteAdminSite']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const upsertAdminSite = createAsyncThunk<string | null, AdminSite, AsyncThunkConfig>(
  'adminSites/upsertAdminSite',
  async (adminSiteToUpsert, thunkAPI) => {
    try {
      const response = (await axios.post('v2_admin_site_upsert', adminSiteToUpsert)) as string; // id;
      return response ?? '';
    } catch (e) {
      logError(e, ['adminSitesSlice', 'upsertAdminSite']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export type ArchiveAdminSiteParam = DeleteAdminSiteParam & { archive: boolean };

export const archiveAdminSite = createAsyncThunk<string | null, ArchiveAdminSiteParam, AsyncThunkConfig>(
  'adminSites/archiveAdminSite',
  async (archiveAdminSiteParam, thunkAPI) => {
    try {
      const response = (await axios.post('v2_admin_site_archive', archiveAdminSiteParam)) as string; // id;
      if (response) {
        notification.success({
          title: `Site ${archiveAdminSiteParam.archive ? 'archived' : 'unarchived'} successfully`,
          description: 'Success',
        });
        await thunkAPI.dispatch(fetchAdminSites());
        thunkAPI.dispatch(closeAdminSiteModal());
      }
      return response ?? '';
    } catch (e) {
      logError(e, ['adminSitesSlice', 'archiveAdminSite']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const adminSitesSlice = createSlice({
  name: 'adminSites',
  initialState,
  reducers: {
    handleSitesTableChange: (state, action: PayloadAction<TableChange<AdminSite>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<AdminSite>;
      if (!sorter.order) {
        sorter.order = null;
      }
      state.requestAdminSitesParam.pagination = action.payload.pagination;
      state.requestAdminSitesParam.sortedInfo = sorter;
      state.requestAdminSitesParam.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.requestAdminSitesParam.pagination = initialState.requestAdminSitesParam.pagination;
      state.requestAdminSitesParam.sortedInfo = initialState.requestAdminSitesParam.sortedInfo;
      state.requestAdminSitesParam.filteredInfo = initialState.requestAdminSitesParam.filteredInfo;
    },
    setAdminSite: (state, action) => {
      state.adminSite = action.payload;
    },
    setAdminSiteModal: (state, action: PayloadAction<ADMIN_SITES_MODAL>) => {
      state.modal = action.payload;
    },
    closeAdminSiteModal: (state) => {
      state.modal = ADMIN_SITES_MODAL.IDLE;
      state.adminSite = createAdminSite();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminSites.pending, (state, _action) => {
        state.loadingAdminSites = true;
      })
      .addCase(fetchAdminSites.fulfilled, (state, action) => {
        state.adminSites = action.payload;
        state.loadingAdminSites = false;
      })
      .addCase(fetchAdminSites.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingAdminSites = false;
        }
      })
      .addCase(fetchAllSites.fulfilled, (state, action) => {
        state.adminSites = action.payload;
      })
      .addCase(upsertAdminSite.pending, (state) => {
        state.submittingAdminSite = true;
      })
      .addCase(upsertAdminSite.fulfilled, (state) => {
        state.submittingAdminSite = false;
      })
      .addCase(upsertAdminSite.rejected, (state) => {
        state.submittingAdminSite = false;
      });
  },
});

export const { handleSitesTableChange, clearFilter, setAdminSite, setAdminSiteModal, closeAdminSiteModal } =
  adminSitesSlice.actions;
